<template>
  <!-- Root is UL -->
  <ul
    v-if="pagesCount > 1"
    v-bind="$attrs"
    :class="['pagination', sizeClass, ...navClasses]"
    role="navigation"
    aria-label="pagination"
  >
    <!-- FIRST -->
    <li
      v-if="!hideGotoEndButtons"
      class="page-item"
      :class="{ disabled: internalPage === 1 }"
    >
      <a
        class="page-link"
        href="#"
        :tabindex="internalPage === 1 ? -1 : 0"
        @click.prevent="goTo(1)"
      >
        <slot name="first-text" :disabled="internalPage === 1">
          {{ firstText }}
        </slot>
      </a>
    </li>

    <!-- PREV -->
    <li class="page-item" :class="{ disabled: internalPage === 1 }">
      <a
        class="page-link"
        href="#"
        :tabindex="internalPage === 1 ? -1 : 0"
        @click.prevent="goTo(internalPage - 1)"
      >
        <slot name="prev-text" :disabled="internalPage === 1">
          {{ prevText }}
        </slot>
      </a>
    </li>

    <!-- ELLIPSIS BEFORE -->
    <li v-if="showStartEllipsis" class="page-item disabled">
      <span class="page-link">…</span>
    </li>

    <!-- PAGE NUMBERS -->
    <li
      v-for="num in pageNumbers"
      :key="num"
      class="page-item"
      :class="{ active: num === internalPage }"
    >
      <a class="page-link" href="#" @click.prevent="goTo(num)">
        <slot
          name="page"
          :page="num"
          :index="num - 1"
          :active="num === internalPage"
        >
          {{ num }}
        </slot>
      </a>
    </li>

    <!-- ELLIPSIS AFTER -->
    <li v-if="showEndEllipsis" class="page-item disabled">
      <span class="page-link">…</span>
    </li>

    <!-- NEXT -->
    <li class="page-item" :class="{ disabled: internalPage === pagesCount }">
      <a
        class="page-link"
        href="#"
        :tabindex="internalPage === pagesCount ? -1 : 0"
        @click.prevent="goTo(internalPage + 1)"
      >
        <slot name="next-text" :disabled="internalPage === pagesCount">
          {{ nextText }}
        </slot>
      </a>
    </li>

    <!-- LAST -->
    <li
      v-if="!hideGotoEndButtons"
      class="page-item"
      :class="{ disabled: internalPage === pagesCount }"
    >
      <a
        class="page-link"
        href="#"
        :tabindex="internalPage === pagesCount ? -1 : 0"
        @click.prevent="goTo(pagesCount)"
      >
        <slot name="last-text" :disabled="internalPage === pagesCount">
          {{ lastText }}
        </slot>
      </a>
    </li>
  </ul>
</template>

<script>
export default {
  name: "BasePagination",
  inheritAttrs: false,
  props: {
    value: { type: Number, default: 1 },
    totalRows: { type: Number, default: 0 },
    perPage: { type: Number, default: null },
    totalPages: { type: Number, default: null },
    hideGotoEndButtons: { type: Boolean, default: false },
    limit: { type: Number, default: 5 },
    size: {
      type: String,
      default: "",
      validator: (v) => ["sm", "lg", ""].includes(v),
    },
    align: {
      type: String,
      default: "left",
      validator: (v) => ["left", "center", "right"].includes(v),
    },
    firstText: { type: String, default: "«" },
    prevText: { type: String, default: "‹" },
    nextText: { type: String, default: "›" },
    lastText: { type: String, default: "»" },
    hideEllipsis: { type: Boolean, default: false },
  },
  computed: {
    pagesCount() {
      if (this.totalPages != null) return this.totalPages;
      if (this.perPage && this.totalRows) {
        return Math.ceil(this.totalRows / this.perPage);
      }
      return 0;
    },
    internalPage: {
      get() {
        return this.value || 1;
      },
      set(v) {
        this.$emit("input", v);
        this.$emit("change", v);
      },
    },
    pageNumbers() {
      const total = this.pagesCount,
        lim = this.limit;
      if (total <= lim || lim < 1) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      let start = this.internalPage - Math.floor(lim / 2);
      if (start < 1) start = 1;
      if (start + lim - 1 > total) start = total - lim + 1;
      return Array.from({ length: lim }, (_, i) => start + i);
    },
    showStartEllipsis() {
      return !this.hideEllipsis && this.pageNumbers[0] > 1;
    },
    showEndEllipsis() {
      const last = this.pageNumbers[this.pageNumbers.length - 1];
      return !this.hideEllipsis && last < this.pagesCount;
    },
    sizeClass() {
      return this.size ? `pagination-${this.size}` : "";
    },
    navClasses() {
      if (this.align === "center") return ["justify-content-center"];
      if (this.align === "right") return ["justify-content-end"];
      return [];
    },
  },
  methods: {
    goTo(page) {
      if (page < 1) page = 1;
      if (page > this.pagesCount) page = this.pagesCount;
      if (page !== this.internalPage) this.internalPage = page;
    },
  },
};
</script>
